// src/components/TopBar.jsx
import React, { useMemo, useRef, useState, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { ChevronDown, LogOut } from 'lucide-react'
import NotificationBell from './NotificationBell.jsx'
import { MODULES } from '../modules/registry.js'
import { apiFetch } from '../api.js'
import { normalizeLogoPath } from '../utils/branding.js'
import fedisLogo from '../assets/fedis.png'

export default function TopBar({ connected, onLogout, user, hasRole, modules, isModuleEnabled }) {
  const navigate = useNavigate()
  const [brandName, setBrandName] = useState('Fedis')
  const [brandLogo, setBrandLogo] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const menuRef = useRef(null)
  const menuBtnRef = useRef(null)
  const menuPanelRef = useRef(null)

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!connected) { setBrandName('Fedis'); setBrandLogo(''); return }
      try {
        const inv = await apiFetch('/api/settings/invoice-format')
        if (!active) return
        setBrandName(String(inv?.company_name || '').trim() || 'Fedis')
        setBrandLogo(normalizeLogoPath(inv?.logo_path))
      } catch { if (!active) return; setBrandName('Fedis'); setBrandLogo('') }
    }
    load()
    window.addEventListener('fedis-branding-updated', load)
    return () => { active = false; window.removeEventListener('fedis-branding-updated', load) }
  }, [connected])

  useEffect(() => {
    if (!menuOpen) return
    const update = () => {
      if (!menuBtnRef.current) return
      const r = menuBtnRef.current.getBoundingClientRect()
      const w = 176
      setMenuPos({ top: r.bottom + 6, left: Math.max(8, r.right - w) })
    }
    const onClick = (e) => {
      if (!menuRef.current?.contains(e.target) && !menuPanelRef.current?.contains(e.target))
        setMenuOpen(false)
    }
    update()
    document.addEventListener('mousedown', onClick)
    window.addEventListener('resize', update)
    return () => { document.removeEventListener('mousedown', onClick); window.removeEventListener('resize', update) }
  }, [menuOpen])

  const roleOk = (allowed) => {
    if (!connected) return false
    if (!allowed?.length) return true
    return allowed.some((r) => hasRole(r))
  }

  const moduleOk = (key) => {
    if (!key) return true
    if (typeof isModuleEnabled === 'function') return isModuleEnabled(key)
    if (modules && modules[key]) return modules[key].enabled !== false
    return true
  }

  const navItems = useMemo(() => {
    const primary = MODULES.filter(
      (m) => m.priority === 'primary' && roleOk(m.roles) && moduleOk(m.key)
    )
    const settings = { key: 'settings', label: 'Paramètres', route: '/settings', roles: ['admin', 'superadmin'] }
    const all = roleOk(settings.roles) ? [settings, ...primary] : primary
    return all
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, user, modules])

  const initials = useMemo(() => {
    const name = user?.username || ''
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return 'U'
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
  }, [user])

  const roleBadge = useMemo(() => {
    if (!connected || !user) return null
    const roles = user?.roles || []
    if (roles.includes('superadmin')) return { label: 'superadmin', cls: 'bg-purple-100 text-purple-800' }
    if (roles.includes('auditor'))    return { label: 'auditor',    cls: 'bg-emerald-100 text-emerald-800' }
    if (roles.includes('admin'))      return { label: 'admin',      cls: 'bg-amber-100 text-amber-800' }
    if (roles.includes('ops'))        return { label: 'ops',        cls: 'bg-sky-100 text-sky-800' }
    if (roles.includes('sales'))      return { label: 'sales',      cls: 'bg-slate-100 text-slate-900' }
    return { label: 'user', cls: 'bg-slate-100 text-slate-700' }
  }, [connected, user])

  return (
    <header className="h-12 shrink-0 bg-white border-b border-slate-200 flex items-center px-4 gap-4 z-20">
      {/* Logo + nom société */}
      <div className="flex items-center gap-2 shrink-0">
        <img
          src={brandLogo || fedisLogo}
          alt={brandName}
          className="h-7 w-7 rounded-lg object-contain ring-1 ring-slate-200"
        />
        <span className="text-[13px] font-extrabold text-slate-900 tracking-tight">{brandName}</span>
      </div>

      <div className="w-px h-5 bg-slate-200 shrink-0" />

      {/* Nav rapide */}
      <nav className="flex items-center gap-1 flex-1 overflow-x-auto scrollbar-none">
        {navItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.route}
            className={({ isActive }) =>
              `shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                isActive
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Droite : cloche + user */}
      {connected && user && (
        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />

          <div ref={menuRef}>
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
            >
              <div className="h-6 w-6 rounded-lg bg-slate-900 text-white flex items-center justify-center text-[10px] font-extrabold">
                {initials}
              </div>
              <span className="text-[12px] font-semibold text-slate-900 max-w-[120px] truncate">
                {user.username}
              </span>
              {roleBadge && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleBadge.cls}`}>
                  {roleBadge.label}
                </span>
              )}
              <ChevronDown size={12} className={`text-slate-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
            </button>

            {menuOpen && typeof document !== 'undefined' && createPortal(
              <div
                ref={menuPanelRef}
                className="fixed z-[100] w-44 rounded-2xl border border-slate-200 bg-white p-1 shadow-lg"
                style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
              >
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate('/mon-compte') }}
                  className="w-full rounded-xl px-3 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  Mon compte
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout() }}
                  className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-left text-[12px] font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut size={13} />
                  Déconnexion
                </button>
              </div>,
              document.body
            )}
          </div>
        </div>
      )}
    </header>
  )
}
